/**
 * ScreenerLanguageBar
 *
 * Two-control toolbar rendered inside the Form Builder's sticky header when
 * the survey-translation feature is enabled:
 *
 *   1. Rebass `Select` — switches which language layer is being edited.
 *      The first option is the language-agnostic primary `form` JSON (tracked
 *      internally as `PRIMARY_LANG_VALUE`). It is labelled "Primary" unless the
 *      author has declared which language the primary is written in via
 *      `default_lang`, in which case that language's name is shown. Only
 *      rendered when at least one translation language exists (i.e., switching
 *      is meaningful).
 *   2. "Languages" `Button` — opens the Manage Languages modal. Its count
 *      includes the primary layer, so one translation reads "Languages (2)".
 *
 * Feature-flagging happens at the call site; this component just renders the
 * controls from the data it receives.
 *
 * @typedef {import('utils/surveyTranslations').ScreenerTranslation} ScreenerTranslation
 */

import React from 'react';
import { Button } from 'rebass';
import { Select } from '@rebass/forms';
import { FiGlobe } from 'react-icons/fi';
import { findLanguageMeta, getDefaultLang } from 'utils/surveyTranslations';
import { PRIMARY_LANG_VALUE } from 'utils/constants';

/**
 * @param {Object} props
 * @param {ScreenerTranslation} props.screener_translation
 * @param {string} props.editingLanguage              `PRIMARY_LANG_VALUE` or a language code
 * @param {(value: string) => void} props.onLanguageChange
 * @param {() => void} props.onOpenManage
 */
function ScreenerLanguageBar({ screener_translation, editingLanguage, onLanguageChange, onOpenManage }) {
    const enabledCodes = Object.keys((screener_translation && screener_translation.lang) || {});
    const defaultLang = getDefaultLang(screener_translation);
    // The primary is language-agnostic; only name it if the author declared one.
    const primaryLabel = defaultLang ? findLanguageMeta(defaultLang).label : 'Primary';
    // Total languages = the primary layer plus each translation. Only surfaced
    // in the button label once a translation exists (no point showing "(1)").
    const totalLanguages = enabledCodes.length + 1;

    return (
        <>
            {enabledCodes.length > 0 && (
                <Select
                    value={editingLanguage}
                    onChange={e => onLanguageChange(e.target.value)}
                    aria-label="Editing language"
                    sx={{ width: 'auto' }}
                >
                    <option value={PRIMARY_LANG_VALUE}>{primaryLabel}</option>
                    {enabledCodes.map(code => (
                        <option key={code} value={code}>
                            {findLanguageMeta(code).label}
                        </option>
                    ))}
                </Select>
            )}
            <Button type="button" variant="secondary-gray" onClick={onOpenManage}>
                <FiGlobe style={{ marginRight: '8px' }} aria-hidden="true" />
                {`Languages${enabledCodes.length > 0 ? ` (${totalLanguages})` : ''}`}
            </Button>
        </>
    );
}

export default ScreenerLanguageBar;
